// @flow strict

import React, { useEffect, useRef, useState, type Node } from 'react';
import { useOnVisible } from '@ta/common.is-visible';
import type { ContainerDimensions, BackgroundSizeCalculator, FitStrategy, Source } from './types';
import AltText from './internal/AltText';
import Loading from './internal/Loading';
import useCachedGetSource from './utils/useCachedGetSource';
import defaultBackgroundSizeCalculator from './utils/defaultBackgroundSizeCalculator';
import getBackgroundSize from './utils/getBackgroundSize';
import getContainerDimensions from './utils/getContainerDimensions';

import styles from './ResponsiveImage.css';

/** Component props for a <ResponsiveImage> */
export type ResponsiveImageProps = {|
  /**
   * The percent the source of the image be allowed to be stretched.
   * Allowing some stretch helps with saving bandwidth, at the cost of quality.
   * Some stretch is fine is most cases, and the bandwidth tradeoff is desireable
   * If you have a special use case where the default is not ok, this property can be set.
   * Setting this to zero means that no stretch is desired (The algorithm will pick an
   * image that is at least as big as the placement).
   */
  allowedStretch?: number,

  /**
   * Alt text for accessibility
   */
  alt?: string,

  /**
   * Background color, when 'contain' or 'calculated' is used
   */
  backgroundColor?: string,

  /**
   * Background position
   */
  backgroundPosition?: string,

  /**
   * The custom fit strategy, see comment above for type BackgroundSizeCalculator
   *  */
  backgroundSizeCalculator?: BackgroundSizeCalculator,

  /** The CSS border-radius for the image. Can be any valid unit/value. */
  borderRadius?: string,

  /**
   * Children of this container
   */
  children?: Node,

  /**
   * Predetermined image containerDimensions. Only use this if image does not change regardless of changes
   * in container size.  Setting this will prevent reflows.  As a consequence, new images will not be
   * selected as container size grows and shrinks.
   */
  containerDimensionsOverride?: ContainerDimensions,

  /**
   * If an image was set, do not remove it.
   * (We can still change the image, but there will always be an image shown).
   */
  doNotRemoveImage?: boolean,

  /**
   *  The fit strategy, see comment above for type FitStrategy
   * */
  fitStrategy?: FitStrategy,

  /** The CSS height of the image. Examples include '20px', '10%', etc. */
  height: string,

  /** Set to true to suppress lazy-load behaviour */
  notLazy?: boolean,

  /**
   * Enable clickable photo to PDP
   */
  onPhotoClick?: (SyntheticEvent<*>) => void,

  /** The initial CSS opacity, will be set to 1 as soon as the image loades */
  opacity?: number,

  /**
   * The CSS padding for the image. Useful to maintain aspect ratio relative to width since padding is relative to the
   * element width.
   */
  padding?: string,

  /**
   * Render something to display while the image is loading
   */
  renderLoading?: () => Node,

  /** An array of 'Source', where each 'Source' contains { url, width, height } */
  sources: Source[],

  /**
   * To force server side rendering: ResponsiveImage relies on container width and height to pick
   * the right size image to save bandwidth. However, this can take some time on the client side,
   * sometimes even seconds on slower devices like mobiles. For images that are critical to be
   * loaded as quickly as possible, this option can be set by passing ContainerDimensions to be used
   * as containerDimensions on the server side.
   */
  ssrContainerDimensions?: ContainerDimensions,

  /**
   * The number of pixels prior to the element coming on screen at which point we want to actually load the
   * image. This applies to both vertical as well as horizontal scrolling. If you are seeing the image popping in
   * as you scroll then you can increase this value to load them more aggressively at the cost of potentially loading
   * images that are never seen.
   */
  threshold?: number,

  /** The CSS transform property */
  transform?: string,

  /** The CSS width of the image. Examples include '20px', '10%', etc. */
  width: string,
|};

// We do not include device pixel ratio as part of this cache because on the server , we default to 1.5 and
// on most phones, it is larger than 2, which unnecessarily generates a new update leading to switching
// to a new image, which we'd like to avoid.

/**
 * A responsive image component that takes a set of images and their sizes, measures the display area, picks the
 * best image to download, and lazy-loads it when scrolled into view.
 *
 * This algorithm to choose the photo first determines the target image size by rendering the target div, measuring it,
 * and converting css pixel measurement to actual pixels (which factors in the devicePixelRatio). It then looks through
 * the images for the smallest one that meets the criteria.
 */
const ResponsiveImage = ({
  allowedStretch,
  alt,
  backgroundColor,
  backgroundPosition,
  borderRadius,
  children,
  backgroundSizeCalculator = defaultBackgroundSizeCalculator,
  doNotRemoveImage = false,
  fitStrategy = 'cover',
  height,
  containerDimensionsOverride,
  notLazy,
  onPhotoClick,
  opacity,
  padding,
  renderLoading,
  sources,
  ssrContainerDimensions,
  threshold = 200,
  transform,
  width,
}: ResponsiveImageProps) => {
  /**
   * If the image is ready to be drawn to the screen (a DOM ref exists, and lazy-load logic is complete or skipped)
   * Initialize as true if ssrContainerDimensions are present
   */
  const [show, setShow] = useState(!!ssrContainerDimensions);

  /**
   * If the image is rendered server side using an approximate container size, use the same during initial render
   * for react hydration to work properly. getImageBasedOnClientInfo is used to track client vs server side render.
   * The contract is for the first client side render to produce the same DOM as when produced by server side render.
   * */
  const [getImageBasedOnClientInfo, setGetImageBasedOnClientInfo] = useState(false);

  const sourceRef = useRef(null);

  const containerRef = useOnVisible<HTMLDivElement | null>(() => setShow(true), threshold);

  // After the client mounts, the component has had at least one client-side-render.
  // It is now ok to allow the component to pick a different image source
  useEffect(() => {
    setGetImageBasedOnClientInfo(true);
  }, []);

  // get the containerDimensions
  const containerDimensions = getContainerDimensions({
    containerDimensionsOverride,
    containerRef,
    getImageBasedOnClientInfo,
    ssrContainerDimensions,
  });

  // if it should be shown then we can pick an image since we can query for the actual size of the container
  const updatedSource = useCachedGetSource({
    allowedStretch,
    containerDimensions,
    getImageBasedOnClientInfo,
    postponeGettingSource: show === false && notLazy !== true,
    sources,
    fitStrategy,
  });

  // only update it if the image is present, we are doing this because sometimes when we set the
  // container that include the image become display: none, it will cause the image to get set to
  // null, and we never update it back when we turn on the display again.
  if (!doNotRemoveImage || updatedSource) {
    sourceRef.current = updatedSource;
  }

  // It is worth noting that we are setting a background image on a "background-size: cover; background-position:
  // center;" div instead of just using the 'object-fit' because it has less browser support.
  const backgroundImage = sourceRef.current ? `url("${sourceRef.current.url}")` : 'none';
  const backgroundSize = getBackgroundSize({
    containerDimensions,
    backgroundSizeCalculator,
    fitStrategy,
    source: sourceRef.current,
  });
  const style = {
    backgroundColor,
    backgroundImage,
    backgroundPosition,
    backgroundSize,
    borderRadius,
    height,
    opacity,
    padding,
    transform,
    width,
  };

  if (show === false && notLazy !== true) {
    return (
      <div className={styles.container} ref={containerRef} style={style}>
        <AltText alt={alt} />
        {children}
      </div>
    );
  }
  return (
    <div className={styles.container} onClick={onPhotoClick} ref={containerRef} style={style}>
      <AltText alt={alt} />
      <Loading renderLoading={renderLoading} url={sourceRef.current?.url} />
      {children}
    </div>
  );
};

export default ResponsiveImage;
